import { Children, isValidElement, useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Copy } from '@phosphor-icons/react';

/** The text inside a fenced block: MDX hands `pre` a single `code` child. */
function codeText(children: ReactNode): { text: string; lang?: string } {
  const code = Children.toArray(children).find(isValidElement) as
    | ReactElement<{ children?: ReactNode; className?: string }>
    | undefined;
  const raw = code?.props.children;
  const text = (typeof raw === 'string' ? raw : Children.toArray(raw).join('')).replace(/\n$/, '');
  const lang = /language-(\S+)/.exec(code?.props.className ?? '')?.[1];
  return { text, lang };
}

/**
 * A fenced code block in a tutorial, with a copy button. The language from the
 * fence (```bash) is shown as a label. The code is in the prerendered HTML;
 * only the button needs JavaScript. The copy button reuses CopyCommand's
 * styles so the two read as one pattern.
 */
export function CodeBlock({ children }: { children?: ReactNode }) {
  const { text, lang } = codeText(children);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard refused: the text stays selectable.
    }
  };

  return (
    <div className="tu-code">
      {lang && <span className="tu-code-lang">{lang}</span>}
      <pre>
        <code>{text}</code>
      </pre>
      <button type="button" className="pg-copy" onClick={copy} data-copied={copied || undefined} aria-label="Copy code">
        <Copy className="pg-copy-idle" size={16} weight="bold" aria-hidden="true" />
        <svg className="pg-copy-done" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M3 8.5l3.2 3.2L13 4.8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <span className="sr-only" aria-live="polite">
        {copied ? 'Copied' : ''}
      </span>
    </div>
  );
}
